// frontend/src/pages/CompanySignupPage.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  useMediaQuery,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import BusinessIcon from "@mui/icons-material/Business";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { validatePassword } from "../utils/validatePassword";
import PasswordRequirements from "../components/PasswordRequirements";

function normalizeNamePart(value) {
  return (value || "")
    .normalize("NFKD")
    .replace(/[^\x00-\x7F]/g, "")
    .replace(/[^A-Za-z\s-]/g, " ")
    .trim();
}

function toCapitalizedWords(value) {
  return normalizeNamePart(value)
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

function buildNormalizedUsername(firstName, lastName) {
  const firstInitial = normalizeNamePart(firstName).charAt(0).toUpperCase();
  const normalizedLast = toCapitalizedWords(lastName);
  if (!firstInitial || !normalizedLast) return "";
  return `${firstInitial}${normalizedLast}`;
}

export default function CompanySignupPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [step, setStep] = useState(1); // 1: Sign up, 3: Success
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [companyData, setCompanyData] = useState({
    company_name: "",
    company_slug: "",
    billing_email: "",
    billing_address: "",
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    is_adt_dealer: "",
    adt_dealer_number: "",
    adt_dealer_legal_name: "",
  });
  const passwordErrors = validatePassword(companyData.password);

  // Auto-generate slug from company name
  const handleCompanyNameChange = (e) => {
    const name = e.target.value;
    setCompanyData((prev) => ({
      ...prev,
      company_name: name,
      company_slug: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
    }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!companyData.company_name.trim()) newErrors.company_name = "Required";
    if (!companyData.billing_email.trim()) newErrors.billing_email = "Required";
    if (!companyData.first_name.trim()) newErrors.first_name = "Required";
    if (!companyData.last_name.trim()) newErrors.last_name = "Required";
    if (!companyData.username.trim()) newErrors.username = "Required";
    if (!companyData.email.trim()) newErrors.email = "Required";
    if (passwordErrors.length)
      newErrors.password = "Please meet all password requirements";
    if (!companyData.is_adt_dealer)
      newErrors.is_adt_dealer = "Please select Yes or No";
    if (companyData.is_adt_dealer === "yes" && !companyData.adt_dealer_number.trim())
      newErrors.adt_dealer_number = "ADT dealer number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    try {
      const payload = {
        company: {
          name: companyData.company_name,
          slug: companyData.company_slug,
          billing_email: companyData.billing_email,
          billing_address: companyData.billing_address || "",
          is_adt_dealer: companyData.is_adt_dealer,
          adt_dealer_number: companyData.adt_dealer_number.trim(),
          adt_dealer_legal_name: companyData.adt_dealer_legal_name.trim(),
        },
        user: {
          first_name: companyData.first_name,
          last_name: companyData.last_name,
          username: companyData.username,
          email: companyData.email,
          password: companyData.password,
        },
      };

      await API.post("/subscriptions/signup/", payload);
      
      toast.success("Account created successfully!");
      setStep(3);
      
      // Auto-login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error("Signup error:", err);
      toast.error(err.response?.data?.detail || "Failed to create account. Please try again.");
      setLoading(false);
    }
  };

  if (step === 3) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "background.default",
        }}
      >
        <Card sx={{ maxWidth: 500, width: "100%", borderRadius: 3 }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Account Created Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your company account has been set up. You will be redirected to login in a moment.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              fullWidth
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ maxWidth: 800, width: "100%" }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <BusinessIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Company Sign Up
          </Typography>
          <Typography variant="body2" color="text.secondary">
            14-day free trial · No credit card required
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(30,41,59,0.88)"
                : "background.paper",
            border: "1px solid",
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 8px 24px rgba(0,0,0,0.55)"
                : "0 4px 12px rgba(0,0,0,0.12)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={3}>
                <Typography variant="h6" fontWeight={700}>
                  Company Information
                </Typography>
                <Divider />

                <TextField
                  label="Company Name"
                  value={companyData.company_name}
                  onChange={handleCompanyNameChange}
                  fullWidth
                  required
                  error={!!errors.company_name}
                  helperText={errors.company_name}
                  size="small"
                />

                <TextField
                  label="Company URL Slug"
                  value={companyData.company_slug}
                  onChange={(e) =>
                    setCompanyData((prev) => ({
                      ...prev,
                      company_slug: e.target.value,
                    }))
                  }
                  fullWidth
                  helperText="Used in your account URL (e.g., yourcompany)"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 1, color: "text.secondary" }}>
                        territorycrm.com/
                      </Typography>
                    ),
                  }}
                />

                <TextField
                  label="Billing Email"
                  type="email"
                  value={companyData.billing_email}
                  onChange={(e) =>
                    setCompanyData((prev) => ({
                      ...prev,
                      billing_email: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.billing_email}
                  helperText={errors.billing_email}
                  size="small"
                />

                <TextField
                  label="Billing Address"
                  multiline
                  rows={2}
                  value={companyData.billing_address}
                  onChange={(e) =>
                    setCompanyData((prev) => ({
                      ...prev,
                      billing_address: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                />

                <Divider />
                <Typography variant="h6" fontWeight={700}>
                  Admin Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                  Create your organization&apos;s admin user. Only one admin user is allowed per organization, and the admin role cannot be removed or reassigned.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="First Name"
                    value={companyData.first_name}
                    onChange={(e) =>
                      setCompanyData((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                        username: buildNormalizedUsername(e.target.value, prev.last_name),
                      }))
                    }
                    fullWidth
                    required
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    size="small"
                  />
                  <TextField
                    label="Last Name"
                    value={companyData.last_name}
                    onChange={(e) =>
                      setCompanyData((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                        username: buildNormalizedUsername(prev.first_name, e.target.value),
                      }))
                    }
                    fullWidth
                    required
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                    size="small"
                  />
                </Stack>

                <TextField
                  label="Username"
                  value={companyData.username}
                  fullWidth
                  required
                  error={!!errors.username}
                  helperText={errors.username || "Auto-generated from first and last name"}
                  size="small"
                  inputProps={{ tabIndex: -1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <LockOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "action.disabledBackground",
                      opacity: 0.85,
                    },
                    "& .MuiInputBase-input": {
                      WebkitTextFillColor: "currentColor",
                      cursor: "not-allowed",
                    },
                  }}
                />

                <TextField
                  label="Email"
                  type="email"
                  value={companyData.email}
                  onChange={(e) =>
                    setCompanyData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                  size="small"
                />

                <TextField
                  label="Password"
                  type="password"
                  value={companyData.password}
                  onChange={(e) =>
                    setCompanyData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.password}
                  helperText={errors.password}
                  size="small"
                  sx={{ mb: 0.5 }}
                />
                <PasswordRequirements password={companyData.password} />
                <Box sx={{ mt: 1.5 }} />

                <Divider />
                <FormControl
                  component="fieldset"
                  required
                  error={!!errors.is_adt_dealer}
                >
                  <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    Authorized ADT dealer?
                  </FormLabel>
                  <RadioGroup
                    row
                    value={companyData.is_adt_dealer}
                    onChange={(e) =>
                      setCompanyData((prev) => ({
                        ...prev,
                        is_adt_dealer: e.target.value,
                        adt_dealer_number:
                          e.target.value === "yes" ? prev.adt_dealer_number : "",
                        adt_dealer_legal_name:
                          e.target.value === "yes" ? prev.adt_dealer_legal_name : "",
                      }))
                    }
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.is_adt_dealer && (
                    <FormHelperText>{errors.is_adt_dealer}</FormHelperText>
                  )}
                </FormControl>

                {companyData.is_adt_dealer === "yes" && (
                  <Stack spacing={2}>
                    <TextField
                      label="ADT dealer number"
                      value={companyData.adt_dealer_number}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          adt_dealer_number: e.target.value,
                        }))
                      }
                      fullWidth
                      required
                      error={!!errors.adt_dealer_number}
                      helperText={
                        errors.adt_dealer_number ||
                        "Required for verified $59/month ADT dealer pricing after trial."
                      }
                      size="small"
                    />
                    <TextField
                      label="Legal dealer name (optional)"
                      value={companyData.adt_dealer_legal_name}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          adt_dealer_legal_name: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                      placeholder="As registered with ADT"
                    />
                  </Stack>
                )}

                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>ADT dealers:</strong> 14-day trial, then $59/month flat after
                    dealer verification. <strong>Other companies:</strong> standard pricing
                    coming soon.
                  </Typography>
                </Alert>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button onClick={() => navigate("/login")} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    endIcon={
                      loading ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />
                    }
                  >
                    {loading ? "Creating account…" : "Start 14-day trial"}
                  </Button>
                </Stack>
              </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}